import { IconButton } from "@dropbox/dig-components/buttons";
import { TextArea } from "@dropbox/dig-components/text_fields";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Split, useTheme } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddLine,
  ArrowUpLine,
  AttachLine,
  EmojiSunglassesLine,
} from "@dropbox/dig-icons/assets";
import { PAP_Send_ComposePrompt } from "@mirage/analytics/events/types/send_compose_prompt";
import { ModelPicker } from "@mirage/mosaics/ConversationAssistant/containers/ModelPicker";
import { useScopedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { publishEvent, SurveyEvent } from "@mirage/service-feedback";
import { KeyCodes } from "@mirage/shared/util/constants";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./ConversationInput.module.css";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type {
  ModelId,
  UserMessageOptions,
} from "@mirage/service-conversation/types";
import type { InputContext } from "@mirage/shared/compose/compose-session";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";

import { createUxaElementId } from "@mirage/analytics/uxa";
import { Link } from "@mirage/link/Link";
import { TranscribingSourcesBanner } from "@mirage/mosaics/ConversationAssistant/containers/TranscribingSourcesBanner";
import { UnavailableSourcesBanner } from "@mirage/mosaics/ConversationAssistant/containers/UnavailableSourcesBanner";
import { useTranscribeFilesStatus } from "@mirage/mosaics/ConversationAssistant/hooks/useTranscribeFiles";
import { useUnavailableFssSources } from "@mirage/service-conversation/hooks/checkFile";
import { reportPapEvent } from "@mirage/service-product-logging";
import { AI_LEARN_MORE_URL } from "@mirage/shared/urls";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import type { ChatConversationMessage, ChatSource } from "../../types";
import { ContextPicker } from "./ContextPicker/ContextPicker";
import { ConversationMessageSourceChips } from "./ConversationMessageSourceChips";

interface ConversationInputProps {
  variant?: "default" | "condensed" | "minimal";
  sources: ChatSource[];
  inputContext?: InputContext;
  setInputContext?: (inputContext: InputContext | undefined) => void;
  onSubmit: (text: string) => void;
  onFocus?: () => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  handleClickAdd?: () => void;
  messages: ChatConversationMessage[];
  showAddSourcesButton?: boolean;
  showModelPickerButton?: boolean;
  showContextPickerButton?: boolean;
  showIncognitoButton?: boolean;
  userMessageOptions?: UserMessageOptions;
  setUserMessageOptions?: (options: UserMessageOptions) => void;
  placeholderOverride?: string;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  selectedModel?: ModelId;
  setSelectedModel?: (model: ModelId) => void;
  conversationId?: string;
  assistRequestId?: string;
  buttonSize?: "medium" | "large";
  isWorkflowAgentPreview?: boolean;
  onRemoveSource?: (source: ChatSource) => void;
  onHeightChange?: (height: number) => void;
  shouldAutoFocusInput?: boolean;
}

export const ConversationInput = memo(
  ({
    variant = "default",
    sources,
    inputContext,
    setInputContext,
    onSubmit,
    onFocus,
    logComposeEvent,
    handleClickAdd,
    messages,
    showAddSourcesButton = true,
    showModelPickerButton = true,
    showContextPickerButton = false,
    showIncognitoButton = false,
    userMessageOptions,
    setUserMessageOptions,
    buttonSize = "medium",
    placeholderOverride,
    disabled = false,
    containerRef: _containerRef,
    selectedModel = "",
    setSelectedModel,
    conversationId,
    assistRequestId,
    isWorkflowAgentPreview = false,
    onRemoveSource,
    onHeightChange,
    shouldAutoFocusInput = true,
  }: ConversationInputProps) => {
    const isModelPickerEnabled = useFeatureFlagValueBool(
      "dash_2025_04_01_conversations_model_picker",
    );
    const { searchQueryUuid } = useSearchQueryId();
    const isFirstUserMessage =
      messages.findIndex((message) => message.role === "user") === -1;

    const textInput = useRef<HTMLTextAreaElement>(null);
    const shouldAutoFocusTextArea = useCallback(() => {
      if (isWorkflowAgentPreview) {
        return false;
      }
      if (!shouldAutoFocusInput) {
        return false;
      }
      const lastMessage = messages[messages.length - 1];
      const hasNoInstructionMessages =
        messages.filter((message) => message.type !== "instruction").length ===
        0;
      const isTypeMessage = lastMessage && lastMessage.type === "message";
      const isWritePrompt =
        isTypeMessage &&
        lastMessage.text.includes(
          i18n.t("compose_assistant_message_partial_match"),
        );
      const isGenericPrompt =
        isTypeMessage &&
        lastMessage.text.includes(i18n.t("compose_assistant_message_generic"));
      const isDoneMessage =
        isTypeMessage && lastMessage.actionContext?.type === "done";
      const lastMessageIsGenericOrPartialMatch =
        isWritePrompt || isGenericPrompt || isDoneMessage;
      const shouldAutoFocus =
        hasNoInstructionMessages || lastMessageIsGenericOrPartialMatch;
      return textInput.current && shouldAutoFocus;
    }, [messages, isWorkflowAgentPreview, shouldAutoFocusInput]);
    useEffect(() => {
      if (shouldAutoFocusTextArea()) {
        textInput?.current?.focus();
      }
    }, [shouldAutoFocusTextArea, sources]);
    const [promptText, setPromptText] = useState("");
    // Track IME (Japanese input) composition state
    const [isComposing, setIsComposing] = useState(false);
    // workaround to set the cursor position after a new line is added when using the altKey for new line
    const desiredCursorPosition = useRef<number | null>(null);
    useEffect(() => {
      if (desiredCursorPosition.current !== null && textInput.current) {
        textInput.current.selectionStart = desiredCursorPosition.current;
        textInput.current.selectionEnd = desiredCursorPosition.current;
        desiredCursorPosition.current = null;
      }
    }, [promptText]);

    // Handle IME (Japanese input) composition start
    const handleCompositionStart = () => {
      setIsComposing(true);
    };

    // Handle IME (Japanese input) composition end
    const handleCompositionEnd = () => {
      setIsComposing(false);
    };

    const { scopedSources } = useScopedSources();
    const fssSources = useMemo(() => {
      return [...scopedSources, ...sources].filter(
        (source) => source.type === "fss_file" || source.type === "fss_folder",
      );
    }, [sources, scopedSources]);
    const { unavailableSources } = useUnavailableFssSources(fssSources);
    const transcribingBannerState = useTranscribeFilesStatus(fssSources);
    const transcriptionInProgress = transcribingBannerState.inProgress > 0;

    const isSendDisabled = useMemo(() => {
      return (
        promptText.trim().length === 0 || disabled || transcriptionInProgress
      );
    }, [promptText, disabled, transcriptionInProgress]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> =
      useCallback(
        (event) => {
          // Skip Enter key handling during IME (Japanese input) composition
          if (event.key === KeyCodes.enter && isComposing) {
            return;
          }

          if (event.key === KeyCodes.enter) {
            if (event.altKey) {
              const cursorPosition = event.currentTarget.selectionStart;
              const newText =
                promptText.slice(0, cursorPosition) +
                "\n" +
                promptText.slice(cursorPosition);
              event.preventDefault();
              desiredCursorPosition.current = cursorPosition + 1;
              setPromptText(newText);
            } else if (!event.shiftKey && promptText.trim().length > 0) {
              event.preventDefault();
              if (isSendDisabled) {
                return;
              }

              onSubmit(promptText);
              setPromptText("");
              if (isWorkflowAgentPreview) {
                reportPapEvent(
                  PAP_Send_ComposePrompt({
                    queryString: promptText,
                    itemCount: messages.length,
                    queryLength: promptText.length,
                    conversationId,
                    searchQueryUuid,
                    assistRequestId,
                    promptType: isFirstUserMessage
                      ? "assistant_first_prompt"
                      : "assistant_subsequent_prompt",
                    actionSurfaceComponent: "ai_tool_creation",
                  }),
                );
              } else {
                logComposeEvent(
                  PAP_Send_ComposePrompt({
                    queryString: promptText,
                    itemCount: messages.length,
                    queryLength: promptText.length,
                    conversationId,
                    searchQueryUuid,
                    assistRequestId,
                    promptType: isFirstUserMessage
                      ? "assistant_first_prompt"
                      : "assistant_subsequent_prompt",
                  }),
                );
              }
              publishEvent(SurveyEvent.SendAssistMessage, {}, true);
            }
          }
          if (event.key === "Backspace" || event.key === "Delete") {
            if (promptText.length === 0) {
              event.preventDefault();
              setInputContext?.(undefined);
            }
          }
        },
        [
          onSubmit,
          promptText,
          setInputContext,
          logComposeEvent,
          messages.length,
          isComposing,
          transcriptionInProgress,
        ],
      );
    const handleTextAreaChange: React.FormEventHandler<HTMLTextAreaElement> =
      useCallback((event) => {
        event.preventDefault();
        setPromptText(event.currentTarget.value);
      }, []);
    const handleSubmit: React.MouseEventHandler = useCallback(
      (event) => {
        event.preventDefault();
        if (!isSendDisabled) {
          if (isWorkflowAgentPreview) {
            reportPapEvent(
              PAP_Send_ComposePrompt({
                queryString: promptText,
                itemCount: messages.length,
                queryLength: promptText.length,
                conversationId,
                searchQueryUuid,
                assistRequestId,
                promptType: isFirstUserMessage
                  ? "assistant_first_prompt"
                  : "assistant_subsequent_prompt",
                actionSurfaceComponent: "ai_tool_creation",
              }),
            );
          } else {
            logComposeEvent(
              PAP_Send_ComposePrompt({
                queryString: promptText,
                itemCount: messages.length,
                queryLength: promptText.length,
                conversationId,
                searchQueryUuid,
                assistRequestId,
                promptType: isFirstUserMessage
                  ? "assistant_first_prompt"
                  : "assistant_subsequent_prompt",
              }),
            );
          }
          onSubmit(promptText);
          setPromptText("");
          publishEvent(SurveyEvent.SendAssistMessage, {}, true);
        }
      },
      [onSubmit, promptText],
    );
    const theme = useTheme();
    // Monitor container width to determine if we should apply compact styling
    const [isContainerVerySmall, setIsContainerVerySmall] = useState(false);
    const containerWidthRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const container = containerWidthRef.current;
      if (!container) return;
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          // Make copy and icons smaller  as the screen size gets narrower
          setIsContainerVerySmall(width < 305);
        }
      });
      resizeObserver.observe(container);
      return () => resizeObserver.disconnect();
    }, []);

    // Strictly used for development purposes to avoid having to delete sessions
    // You must be in the feature flag group to see this button
    const isIncognitoMode = userMessageOptions?.skipPersistence ?? false;
    const handleToggleIncognito = useCallback(() => {
      if (setUserMessageOptions && userMessageOptions) {
        setUserMessageOptions({
          ...userMessageOptions,
          skipPersistence: !isIncognitoMode,
        });
      }
    }, [setUserMessageOptions, userMessageOptions, isIncognitoMode]);

    const shouldShowAddSourcesButton = showAddSourcesButton && handleClickAdd;
    const shouldShowModelPicker =
      isModelPickerEnabled && showModelPickerButton && setSelectedModel;
    const shouldShowContextPicker =
      showContextPickerButton && setUserMessageOptions;
    const shouldShowIncognitoButton =
      showIncognitoButton && setUserMessageOptions;
    const shouldShowActions =
      variant !== "condensed" &&
      (shouldShowAddSourcesButton ||
        shouldShowModelPicker ||
        shouldShowContextPicker);

    // Track the height of the conversation input component to know where to put the scroll indicator
    const componentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (!componentRef.current || !onHeightChange) return;
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          onHeightChange(entry.contentRect.height);
        }
      });
      resizeObserver.observe(componentRef.current);
      onHeightChange(componentRef.current.offsetHeight);
      return () => resizeObserver.disconnect();
    }, [onHeightChange]);

    const addSourcesButton = (
      <IconButton
        variant="opacity"
        shape="circular"
        onClick={handleClickAdd}
        aria-label={i18n.t("compose_add_source_action")}
        data-testid="Add source"
      >
        <UIIcon
          className={styles.ConversationInputAddSourcesIconMobile}
          src={isEmbedPage() ? AttachLine : AddLine}
        />
      </IconButton>
    );

    return (
      <>
        <div
          ref={componentRef}
          className={classnames(styles.ConversationInput, {
            [styles.ConversationInputCondensed]: variant === "condensed",
          })}
        >
          {unavailableSources.length > 0 && (
            <UnavailableSourcesBanner unavailableSources={unavailableSources} />
          )}
          {transcriptionInProgress && (
            <TranscribingSourcesBanner {...transcribingBannerState} />
          )}
          <div
            className={styles.ConversationInputContainer}
            ref={containerWidthRef}
          >
            {shouldShowIncognitoButton && (
              <DigTooltip
                title={
                  isIncognitoMode
                    ? i18n.t("conversation_incognito_mode_disable")
                    : i18n.t("conversation_incognito_mode_enable")
                }
              >
                <IconButton
                  variant="borderless"
                  shape="circular"
                  onClick={handleToggleIncognito}
                  aria-label={
                    isIncognitoMode
                      ? i18n.t("conversation_incognito_mode_disable")
                      : i18n.t("conversation_incognito_mode_enable")
                  }
                  className={classnames(
                    styles.ConversationInputIncognitoButton,
                    {
                      [styles.ConversationInputIncognitoButtonActive]:
                        isIncognitoMode,
                    },
                  )}
                >
                  <UIIcon src={EmojiSunglassesLine} size="small" />
                </IconButton>
              </DigTooltip>
            )}
            {sources && sources.length > 0 && onRemoveSource && (
              <div
                className={classnames(styles.ConversationInputSources, {
                  [styles.Compact]: isEmbedPage(),
                  [styles.LikelyOverflow]: sources.length > 3 && isEmbedPage(),
                })}
              >
                <ConversationMessageSourceChips
                  sources={sources}
                  removeSource={onRemoveSource}
                  logComposeEvent={logComposeEvent}
                  variant="full"
                />
              </div>
            )}
            <div className={styles.ConversationInputs}>
              {inputContext && (
                <Text className={styles.ConversationInputContext}>
                  <Truncate lines={2}>{inputContext.selectedText}</Truncate>
                </Text>
              )}
              <TextArea
                ref={textInput}
                className={classnames(styles.ConversationInputTextArea, {
                  [styles.ConversationInputTextAreaMinimal]:
                    variant === "minimal",
                })}
                placeholder={
                  placeholderOverride ||
                  getPlaceholderText(variant, messages.length, sources.length)
                }
                rows={1}
                resizable="auto"
                isTransparent
                value={promptText}
                onChange={handleTextAreaChange}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
              />
              {shouldShowActions && (
                <div className={styles.ConversationInputActions}>
                  <Split gap="16px" alignY="center">
                    <Split.Item>
                      {shouldShowAddSourcesButton && addSourcesButton}
                    </Split.Item>
                    {shouldShowModelPicker && (
                      <Split.Item>
                        <ModelPicker
                          selectedModel={selectedModel}
                          setSelectedModel={setSelectedModel}
                          logComposeEvent={logComposeEvent}
                          conversationId={conversationId}
                          variant="transparent"
                          isCompact={isContainerVerySmall}
                        />
                      </Split.Item>
                    )}
                  </Split>
                  {showContextPickerButton && setUserMessageOptions && (
                    <ContextPicker
                      userMessageOptions={userMessageOptions}
                      setUserMessageOptions={setUserMessageOptions}
                      logComposeEvent={logComposeEvent}
                      conversationId={conversationId}
                      displayMessages={messages}
                      manuallyAddedSources={sources}
                      isCompact={isContainerVerySmall}
                      selectedModel={selectedModel}
                      setSelectedModel={setSelectedModel}
                      handleClickAdd={handleClickAdd}
                      assistRequestId={assistRequestId}
                    />
                  )}
                </div>
              )}
            </div>
            <IconButton
              className={classnames(
                theme.getInverseProps().className,
                styles.ConversationInputSendButton,
                {
                  [styles.ConversationInputSendButtonBottomRight]:
                    variant !== "minimal",
                },
              )}
              variant="filled"
              shape="circular"
              size={buttonSize}
              aria-label={i18n.t("send")}
              onClick={handleSubmit}
              disabled={isSendDisabled}
            >
              <UIIcon
                src={ArrowUpLine}
                size="small"
                className={styles.ConversationInputSendButtonIcon}
              />
            </IconButton>
          </div>
        </div>
        <Text className={styles.ConversationInputDisclaimer}>
          {i18n.t("assist_ai_prompt_disclaimer")}
          &nbsp;
          <Link
            variant="monochromatic"
            href={AI_LEARN_MORE_URL}
            target="_blank"
            data-uxa-log={createUxaElementId("learn_more_link", {
              actionSurfaceComponent: "inline_answer",
              featureLine: "multi_answers",
            })}
          >
            {i18n.t("assist_ai_prompt_external_link")}
          </Link>
        </Text>
      </>
    );
  },
);
ConversationInput.displayName = "ConversationInput";

export function getPlaceholderText(
  variant: ConversationInputProps["variant"],
  messagesCount: number,
  sourcesCount: number,
): string {
  switch (variant) {
    case "condensed":
      return i18n.t("compose_prompt_dash_placeholder_chat");
    case "minimal":
      return i18n.t("compose_prompt_dash_placeholder_minimal");
    default:
      if (sourcesCount > 0) {
        if (sourcesCount > 1) {
          return i18n.t("compose_prompt_dash_placeholder_chat_with_sources", {
            count: sourcesCount,
          });
        } else {
          return i18n.t("compose_prompt_dash_placeholder_chat_with_source");
        }
      } else if (messagesCount !== 0) {
        return i18n.t("compose_prompt_dash_placeholder_chat");
      } else {
        return i18n.t("compose_prompt_dash_placeholder");
      }
  }
}
